import React, { useLayoutEffect, useRef, useState } from 'react';
import useClickOutside from '../hooks/useClickOutside';
import type { AnalysisResult } from '../types';

interface User {
  name?: string;
  email?: string;
}

interface SidebarProps {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  analysisHistory: AnalysisResult[];
  onSelectHistoryItem: (item: AnalysisResult) => void;
  onNewAnalysis: () => void;
  displayName?: string;
  user?: User | null;
  onLogout: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  sidebarOpen,
  onToggleSidebar,
  analysisHistory,
  onSelectHistoryItem,
  onNewAnalysis,
  displayName,
  user,
  onLogout,
}) => {
  const [showUserMenu, setShowUserMenu] = useState<boolean>(false);
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties | null>(null);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  useClickOutside(popupRef, () => setShowUserMenu(false), showUserMenu);

  // Compute popup position and keep it inside the viewport
  const computePopupPosition = () => {
    const btn = buttonRef.current;
    const popup = popupRef.current;
    if (!btn || !popup) return;

    const margin = 8;
    const btnRect = btn.getBoundingClientRect();
    const popupRect = popup.getBoundingClientRect();

    let left = btnRect.left + btnRect.width / 2 - popupRect.width / 2;
    let top = btnRect.bottom + margin;

    // If space above the button, place it above
    if (btnRect.top - popupRect.height - margin >= margin) {
      top = btnRect.top - popupRect.height - margin;
    }

    // Clamp within viewport horizontally
    const vw = window.innerWidth;
    if (left + popupRect.width + margin > vw) {
      left = vw - popupRect.width - margin;
    }
    if (left < margin) {
      left = margin;
    }

    setPopupStyle({ position: 'fixed', left: `${Math.round(left)}px`, top: `${Math.round(top)}px` });
  };

  useLayoutEffect(() => {
    if (!showUserMenu) return;

    computePopupPosition();

    let raf = 0 as number | undefined;
    let timeoutId: number | undefined;
    raf = window.requestAnimationFrame(() => {
      computePopupPosition();
      timeoutId = window.setTimeout(() => computePopupPosition(), 50);
    });

    const onResize = () => computePopupPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
      if (raf) window.cancelAnimationFrame(raf);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [showUserMenu, sidebarOpen]);

  return (
    <div 
      className={`
        ${sidebarOpen ? 'w-80' : 'w-16'} 
        transition-all duration-300 ease-in-out
        border-r border-border-primary bg-bg-primary 
        flex flex-col
        h-screen
      `}
    >
      {/* Sidebar Header */}
      <div className="p-4 border-b border-border-primary flex-shrink-0 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={onToggleSidebar}
            className="w-10 h-10 p-0 hover:bg-bg-tertiary rounded-lg transition-colors flex items-center justify-center"
            aria-label="Toggle sidebar"
          >
            <div className="p-1 flex items-center justify-center">
              <svg className="w-5 h-5 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </div>
          </button>
        </div>

        {/* Plus button */}
        <div className={`${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-200`}> 
          <button
            onClick={onNewAnalysis}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-bg-secondary hover:bg-bg-tertiary border border-border-secondary shadow-sm hover:shadow-md transform hover:-translate-y-[1px] transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-border-focus"
            aria-label="New analysis"
            title="Start a new analysis"
          >
            <svg className="w-4 h-4 text-text-secondary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span className="sr-only">Start a new analysis</span>
          </button>
        </div>
      </div>

      {/* Analysis History */}
      <div className={`flex-1 overflow-y-auto transition-opacity duration-300 ease-in-out ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <div className="p-4">
            <h3 className="text-sm font-medium text-text-tertiary mb-4">Recent Analysis</h3>
            <div className="space-y-2">
              {analysisHistory.length === 0 ? (
                <p className="text-sm text-text-muted italic">No recent analysis</p>
              ) : (
                analysisHistory.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => onSelectHistoryItem(item)}
                    className="w-full text-left p-3 hover:bg-bg-secondary rounded-lg transition-colors border border-border-secondary"
                  >
                    <div className="text-sm font-medium text-text-secondary mb-1 truncate">
                      {item.categoryName}
                    </div>
                    <div className="text-xs text-text-tertiary mb-2">
                      {new Date(item.timestamp).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-text-muted line-clamp-2">
                      {item.analysis.substring(0, 100)}...
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

      {/* User Menu */}
      <div className="p-4 border-t border-border-primary flex-shrink-0">
        <div
          ref={buttonRef}
          onClick={() => setShowUserMenu(!showUserMenu)}
          role="button"
          tabIndex={0}
          className={`w-full flex items-center gap-3 h-10 select-none cursor-pointer`}
          aria-label="User menu"
        >
          {/* Avatar */}
          <div className="w-10 h-10 p-0 flex items-center justify-center">
            <div className="p-1 flex items-center justify-center">
              <div className="flex-none w-8 h-8 bg-brand-yellow rounded-lg flex items-center justify-center">
                <span className="text-text-black text-sm font-medium">
                  {displayName?.charAt(0) || user?.name?.charAt(0) || 'U'}
                </span>
              </div>
            </div>
          </div>

          {/* Name/email */}
          <div className={`flex-1 text-left overflow-hidden transition-opacity duration-300 ease-in-out ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className="text-sm font-medium text-text-secondary truncate">
              {displayName || user?.name || 'User'}
            </div>
            <div className="text-xs text-text-tertiary truncate">
              {user?.email}
            </div>
          </div>
        </div>

        {/* User Menu Popup */}
        {showUserMenu && (
          <div
            ref={popupRef}
            style={popupStyle || undefined}
            className="bg-bg-primary border border-border-secondary rounded-md shadow-2xl py-1 z-[9999] min-w-[200px]"
          >
            <div className="px-4 py-2 border-b border-border-secondary">
              <div className="text-sm font-medium text-text-secondary">
                {displayName || user?.name || 'User'}
              </div>
              <div className="text-xs text-text-tertiary">
                {user?.email}
              </div>
            </div>
            <button
              onClick={() => {
                setShowUserMenu(false);
                onLogout();
              }}
              className="w-full text-left px-3 py-2 text-sm text-text-secondary hover:bg-bg-secondary transition-colors"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
